import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, CheckCircle2, XCircle, PlayCircle } from 'lucide-react';

// ── V-C1 types ───────────────────────────────────────────────────────────
type CaseResult = {
  name: string;
  status: number;
  expected_error: string;
  actual_error: string | null;
  content_type_json: boolean;
  no_token_leak: boolean;
  no_dynvars_leak: boolean;
  passed: boolean;
};

type Teardown = {
  ok: boolean;
  deleted_counts: Record<string, number>;
  errors: string[];
};

type Vc1Response = {
  ok: boolean;
  cases?: CaseResult[];
  teardown?: Teardown;
  error?: string;
  detail?: string;
};

// ── V-C2 types ───────────────────────────────────────────────────────────
type Assertion = { name: string; passed: boolean; detail: string };

type Vc2Response = {
  ok: boolean;
  http_status?: number;
  content_type?: string;
  token_shape?: { length: number; dot_count: number };
  assertions?: Assertion[];
  teardown?: Teardown;
  error?: string;
  detail?: string;
};

// ── V-C3 types ───────────────────────────────────────────────────────────
type Vc3Response = {
  ok: boolean;
  assertions?: Assertion[];
  teardown?: Teardown;
  error?: string;
  detail?: string;
};

// ── V-C4 types ───────────────────────────────────────────────────────────
type Vc4AssertionStatus = 'passed' | 'failed' | 'skipped_contract_absent';
type Vc4Assertion = { name: string; status: Vc4AssertionStatus; detail: string };
type Vc4Contract = {
  scope: string | null;
  limit: number;
  window_seconds: number;
  expected_status: number | null;
  expected_error_code: string | null;
  summary: string;
};
type Vc4Response = {
  ok: boolean;
  contract_detected?: Vc4Contract;
  assertions?: Vc4Assertion[];
  teardown?: Teardown;
  error?: string;
  detail?: string;
};

// ── V-C4 Burst types ─────────────────────────────────────────────────────
type Vc4bContract = {
  scope: string;
  limit: number;
  window_seconds: number;
  burst_per_user: number;
  summary: string;
};
type Vc4bResponse = {
  ok: boolean;
  contract?: Vc4bContract;
  assertions?: Vc4Assertion[];
  teardown?: Teardown;
  error?: string;
  detail?: string;
};

function TeardownCard({ td }: { td: Teardown }) {
  return (
    <Card className={td.ok ? '' : 'border-destructive'}>
      <CardHeader>
        <div className="flex items-center gap-2">
          {td.ok ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          ) : (
            <XCircle className="h-4 w-4 text-destructive" />
          )}
          <CardTitle className="text-base font-heading">Teardown</CardTitle>
        </div>
        <CardDescription>Rows deleted while reclaiming the fixture.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-mono">
          {Object.entries(td.deleted_counts).map(([k, v]) => (
            <div key={k} className="flex justify-between rounded border border-border px-2 py-1">
              <span className="text-muted-foreground truncate">{k}</span>
              <span className="font-medium">{v}</span>
            </div>
          ))}
        </div>
        {td.errors.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-medium text-destructive">Errors</p>
            <ul className="text-xs font-mono text-destructive space-y-0.5">
              {td.errors.map((e, i) => <li key={i}>• {e}</li>)}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Vc1Panel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Vc1Response | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);

  const run = async () => {
    setLoading(true); setError(null); setResult(null); setElapsedMs(null);
    const t0 = Date.now();
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke<Vc1Response>(
        'phase4a-vc1-gauntlet', { body: {} },
      );
      if (invokeErr) setError(invokeErr.message || 'Invocation failed');
      else setResult(data ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setElapsedMs(Date.now() - t0);
      setLoading(false);
    }
  };

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Phase 4A · V-C1 gauntlet</h2>
          <p className="text-sm text-muted-foreground">
            Runs <code className="text-xs bg-muted px-1 py-0.5 rounded">phase4a-vc1-gauntlet</code>.
            Three rejection paths against <code className="text-xs bg-muted px-1 py-0.5 rounded">pulse-conversation-token</code>.
          </p>
        </div>
        <Button onClick={run} disabled={loading} size="lg">
          {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>) :
            (<><PlayCircle className="h-4 w-4 mr-2" /> Run V-C1</>)}
        </Button>
      </div>

      {elapsedMs !== null && (
        <p className="text-xs text-muted-foreground">Last run: {(elapsedMs / 1000).toFixed(2)}s</p>
      )}

      {error && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Invocation error</CardTitle>
            <CardDescription className="font-mono text-xs">{error}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {result && (
        <>
          <Card className={result.ok ? 'border-emerald-500/40' : 'border-destructive'}>
            <CardHeader>
              <div className="flex items-center gap-2">
                {result.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> :
                  <XCircle className="h-5 w-5 text-destructive" />}
                <CardTitle className="text-base">
                  {result.ok ? 'All checks passed' : 'Failed'}
                </CardTitle>
              </div>
              {result.error && (
                <CardDescription className="font-mono text-xs">
                  {result.error}{result.detail ? ` — ${result.detail}` : ''}
                </CardDescription>
              )}
            </CardHeader>
          </Card>

          {result.cases && result.cases.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-heading">Cases</CardTitle>
                <CardDescription>
                  Each case must return HTTP 401 with the expected error code and no token / dynamic_variables leak.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {result.cases.map((c) => (
                  <div key={c.name} className="rounded-md border border-border p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {c.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> :
                          <XCircle className="h-4 w-4 text-destructive" />}
                        <span className="font-mono text-sm font-medium">{c.name}</span>
                      </div>
                      <Badge variant={c.passed ? 'default' : 'destructive'}>
                        {c.passed ? 'passed' : 'failed'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs font-mono">
                      <div><span className="text-muted-foreground">status:</span> {c.status}</div>
                      <div><span className="text-muted-foreground">expected:</span> {c.expected_error}</div>
                      <div><span className="text-muted-foreground">actual:</span> {c.actual_error ?? '∅'}</div>
                      <div><span className="text-muted-foreground">json:</span> {c.content_type_json ? '✓' : '✗'}</div>
                      <div><span className="text-muted-foreground">no token:</span> {c.no_token_leak ? '✓' : '✗'}</div>
                      <div><span className="text-muted-foreground">no dynvars:</span> {c.no_dynvars_leak ? '✓' : '✗'}</div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {result.teardown && <TeardownCard td={result.teardown} />}
        </>
      )}
    </section>
  );
}

function Vc2Panel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Vc2Response | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);

  const run = async () => {
    setLoading(true); setError(null); setResult(null); setElapsedMs(null);
    const t0 = Date.now();
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke<Vc2Response>(
        'phase4a-vc2-gauntlet', { body: {} },
      );
      if (invokeErr) setError(invokeErr.message || 'Invocation failed');
      else setResult(data ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setElapsedMs(Date.now() - t0);
      setLoading(false);
    }
  };

  return (
    <section className="space-y-6 pt-4 border-t border-border">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Phase 4A · V-C2 gauntlet</h2>
          <p className="text-sm text-muted-foreground">
            Runs <code className="text-xs bg-muted px-1 py-0.5 rounded">phase4a-vc2-gauntlet</code>.
            Happy-path issuance: provisions a synthetic employee, calls{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">pulse-conversation-token</code>,
            and asserts the success contract (token + scoped dynvars + no PII leak).
          </p>
        </div>
        <Button onClick={run} disabled={loading} size="lg">
          {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>) :
            (<><PlayCircle className="h-4 w-4 mr-2" /> Run V-C2</>)}
        </Button>
      </div>

      {elapsedMs !== null && (
        <p className="text-xs text-muted-foreground">Last run: {(elapsedMs / 1000).toFixed(2)}s</p>
      )}

      {error && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Invocation error</CardTitle>
            <CardDescription className="font-mono text-xs">{error}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {result && (
        <>
          <Card className={result.ok ? 'border-emerald-500/40' : 'border-destructive'}>
            <CardHeader>
              <div className="flex items-center gap-2">
                {result.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> :
                  <XCircle className="h-5 w-5 text-destructive" />}
                <CardTitle className="text-base">
                  {result.ok ? 'All checks passed' : 'Failed'}
                </CardTitle>
              </div>
              {result.error && (
                <CardDescription className="font-mono text-xs">
                  {result.error}{result.detail ? ` — ${result.detail}` : ''}
                </CardDescription>
              )}
            </CardHeader>
            {(result.http_status !== undefined || result.token_shape) && (
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs font-mono">
                  <div><span className="text-muted-foreground">http:</span> {result.http_status ?? '∅'}</div>
                  <div className="truncate"><span className="text-muted-foreground">ct:</span> {result.content_type ?? '∅'}</div>
                  <div><span className="text-muted-foreground">tok.len:</span> {result.token_shape?.length ?? 0}</div>
                  <div><span className="text-muted-foreground">tok.dots:</span> {result.token_shape?.dot_count ?? 0}</div>
                </div>
              </CardContent>
            )}
          </Card>

          {result.assertions && result.assertions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-heading">Assertions</CardTitle>
                <CardDescription>
                  Success contract for <code className="text-xs">pulse-conversation-token</code>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {result.assertions.map((a) => (
                  <div key={a.name} className="rounded-md border border-border p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {a.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> :
                          <XCircle className="h-4 w-4 text-destructive" />}
                        <span className="font-mono text-sm font-medium">{a.name}</span>
                      </div>
                      <Badge variant={a.passed ? 'default' : 'destructive'}>
                        {a.passed ? 'passed' : 'failed'}
                      </Badge>
                    </div>
                    <div className="text-xs font-mono text-muted-foreground break-all">{a.detail}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {result.teardown && <TeardownCard td={result.teardown} />}
        </>
      )}
    </section>
  );
}

function Vc3Panel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Vc3Response | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);

  const run = async () => {
    setLoading(true); setError(null); setResult(null); setElapsedMs(null);
    const t0 = Date.now();
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke<Vc3Response>(
        'phase4a-vc3-gauntlet', { body: {} },
      );
      if (invokeErr) setError(invokeErr.message || 'Invocation failed');
      else setResult(data ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setElapsedMs(Date.now() - t0);
      setLoading(false);
    }
  };

  return (
    <section className="space-y-6 pt-4 border-t border-border">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Phase 4A · V-C3 gauntlet</h2>
          <p className="text-sm text-muted-foreground">
            Runs <code className="text-xs bg-muted px-1 py-0.5 rounded">phase4a-vc3-gauntlet</code>.
            Cross-tenant isolation + token misuse: two orgs, two employees, five assertions
            covering distinct tokens, scoped dynvars, handle isolation, freshness, and
            revoked-membership blocking.
          </p>
        </div>
        <Button onClick={run} disabled={loading} size="lg">
          {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>) :
            (<><PlayCircle className="h-4 w-4 mr-2" /> Run V-C3</>)}
        </Button>
      </div>

      {elapsedMs !== null && (
        <p className="text-xs text-muted-foreground">Last run: {(elapsedMs / 1000).toFixed(2)}s</p>
      )}

      {error && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Invocation error</CardTitle>
            <CardDescription className="font-mono text-xs">{error}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {result && (
        <>
          <Card className={result.ok ? 'border-emerald-500/40' : 'border-destructive'}>
            <CardHeader>
              <div className="flex items-center gap-2">
                {result.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> :
                  <XCircle className="h-5 w-5 text-destructive" />}
                <CardTitle className="text-base">
                  {result.ok ? 'All checks passed' : 'Failed'}
                </CardTitle>
              </div>
              {result.error && (
                <CardDescription className="font-mono text-xs">
                  {result.error}{result.detail ? ` — ${result.detail}` : ''}
                </CardDescription>
              )}
            </CardHeader>
          </Card>

          {result.assertions && result.assertions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-heading">Assertions</CardTitle>
                <CardDescription>
                  Cross-tenant isolation contract for <code className="text-xs">pulse-conversation-token</code>.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {result.assertions.map((a) => (
                  <div key={a.name} className="rounded-md border border-border p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {a.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> :
                          <XCircle className="h-4 w-4 text-destructive" />}
                        <span className="font-mono text-sm font-medium">{a.name}</span>
                      </div>
                      <Badge variant={a.passed ? 'default' : 'destructive'}>
                        {a.passed ? 'passed' : 'failed'}
                      </Badge>
                    </div>
                    <div className="text-xs font-mono text-muted-foreground break-all">{a.detail}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {result.teardown && <TeardownCard td={result.teardown} />}
        </>
      )}
    </section>
  );
}

function Vc4Panel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Vc4Response | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);

  const run = async () => {
    setLoading(true); setError(null); setResult(null); setElapsedMs(null);
    const t0 = Date.now();
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke<Vc4Response>(
        'phase4a-vc4-gauntlet', { body: {} },
      );
      if (invokeErr) setError(invokeErr.message || 'Invocation failed');
      else setResult(data ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setElapsedMs(Date.now() - t0);
      setLoading(false);
    }
  };

  const badgeFor = (s: Vc4AssertionStatus) => {
    if (s === 'passed') return <Badge>passed</Badge>;
    if (s === 'failed') return <Badge variant="destructive">failed</Badge>;
    return <Badge variant="secondary">skipped</Badge>;
  };
  const iconFor = (s: Vc4AssertionStatus) => {
    if (s === 'passed') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (s === 'failed') return <XCircle className="h-4 w-4 text-destructive" />;
    return <XCircle className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <section className="space-y-6 pt-4 border-t border-border">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Phase 4A · V-C4 gauntlet</h2>
          <p className="text-sm text-muted-foreground">
            Runs <code className="text-xs bg-muted px-1 py-0.5 rounded">phase4a-vc4-gauntlet</code>.
            Rate-limit / abuse protection on{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">pulse-conversation-token</code>.
            Two-employee fixture (orgA emp1, orgA emp2).
          </p>
        </div>
        <Button onClick={run} disabled={loading} size="lg">
          {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>) :
            (<><PlayCircle className="h-4 w-4 mr-2" /> Run V-C4</>)}
        </Button>
      </div>

      {elapsedMs !== null && (
        <p className="text-xs text-muted-foreground">Last run: {(elapsedMs / 1000).toFixed(2)}s</p>
      )}

      {error && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Invocation error</CardTitle>
            <CardDescription className="font-mono text-xs">{error}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {result && (
        <>
          <Card className={result.ok ? 'border-emerald-500/40' : 'border-destructive'}>
            <CardHeader>
              <div className="flex items-center gap-2">
                {result.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> :
                  <XCircle className="h-5 w-5 text-destructive" />}
                <CardTitle className="text-base">
                  {result.ok ? 'All checks passed' : 'Failed'}
                </CardTitle>
              </div>
              {result.contract_detected && (
                <CardDescription className="font-mono text-xs pt-1">
                  contract: {result.contract_detected.summary}
                </CardDescription>
              )}
              {result.error && (
                <CardDescription className="font-mono text-xs">
                  {result.error}{result.detail ? ` — ${result.detail}` : ''}
                </CardDescription>
              )}
            </CardHeader>
          </Card>

          {result.assertions && result.assertions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-heading">Assertions</CardTitle>
                <CardDescription>
                  Rate-limit contract for <code className="text-xs">pulse-conversation-token</code>.
                  Skipped cases reflect an absent runtime contract — not a test failure.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {result.assertions.map((a) => (
                  <div key={a.name} className="rounded-md border border-border p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {iconFor(a.status)}
                        <span className="font-mono text-sm font-medium">{a.name}</span>
                      </div>
                      {badgeFor(a.status)}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground break-all">{a.detail}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {result.teardown && <TeardownCard td={result.teardown} />}
        </>
      )}
    </section>
  );
}
function Vc4BurstPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Vc4bResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);

  const run = async () => {
    setLoading(true); setError(null); setResult(null); setElapsedMs(null);
    const t0 = Date.now();
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke<Vc4bResponse>(
        'phase4a-vc4-burst-gauntlet', { body: {} },
      );
      if (invokeErr) setError(invokeErr.message || 'Invocation failed');
      else setResult(data ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setElapsedMs(Date.now() - t0);
      setLoading(false);
    }
  };

  const badgeFor = (s: Vc4AssertionStatus) => {
    if (s === 'passed') return <Badge>passed</Badge>;
    if (s === 'failed') return <Badge variant="destructive">failed</Badge>;
    return <Badge variant="secondary">skipped</Badge>;
  };
  const iconFor = (s: Vc4AssertionStatus) => {
    if (s === 'passed') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (s === 'failed') return <XCircle className="h-4 w-4 text-destructive" />;
    return <XCircle className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <section className="space-y-6 pt-4 border-t border-border">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Phase 4A · V-C4 Burst gauntlet</h2>
          <p className="text-sm text-muted-foreground">
            Runs <code className="text-xs bg-muted px-1 py-0.5 rounded">phase4a-vc4-burst-gauntlet</code>.
            Concurrency stress of the rate-limit contract via parallel fan-out
            (<code className="text-xs">Promise.all</code>, 20 reqs/user). Three-employee fixture
            (emp1/emp2/emp3 in orgA) — emp3 exists only in this lane so the fresh-bucket probe
            does not couple to teardown internals.
          </p>
        </div>
        <Button onClick={run} disabled={loading} size="lg">
          {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>) :
            (<><PlayCircle className="h-4 w-4 mr-2" /> Run V-C4 Burst</>)}
        </Button>
      </div>

      {elapsedMs !== null && (
        <p className="text-xs text-muted-foreground">Last run: {(elapsedMs / 1000).toFixed(2)}s</p>
      )}

      {error && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Invocation error</CardTitle>
            <CardDescription className="font-mono text-xs">{error}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {result && (
        <>
          <Card className={result.ok ? 'border-emerald-500/40' : 'border-destructive'}>
            <CardHeader>
              <div className="flex items-center gap-2">
                {result.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> :
                  <XCircle className="h-5 w-5 text-destructive" />}
                <CardTitle className="text-base">
                  {result.ok ? 'All checks passed' : 'Failed'}
                </CardTitle>
              </div>
              {result.contract && (
                <CardDescription className="font-mono text-xs pt-1">
                  contract: {result.contract.summary}
                </CardDescription>
              )}
              {result.error && (
                <CardDescription className="font-mono text-xs">
                  {result.error}{result.detail ? ` — ${result.detail}` : ''}
                </CardDescription>
              )}
            </CardHeader>
          </Card>

          {result.assertions && result.assertions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-heading">Assertions</CardTitle>
                <CardDescription>
                  Concurrency contract for <code className="text-xs">pulse-conversation-token</code>{' '}
                  + <code className="text-xs">pulse_rate_limit_hit</code>. Exact 10/10 split asserts
                  row-lock atomicity (no lost increments).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {result.assertions.map((a) => (
                  <div key={a.name} className="rounded-md border border-border p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {iconFor(a.status)}
                        <span className="font-mono text-sm font-medium">{a.name}</span>
                      </div>
                      {badgeFor(a.status)}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground break-all">{a.detail}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {result.teardown && <TeardownCard td={result.teardown} />}
        </>
      )}
    </section>
  );
}

// ── V-D1 types ───────────────────────────────────────────────────────────
type Vd1AssertionStatus = 'passed' | 'failed' | 'skipped_contract_absent';
type Vd1Assertion = { name: string; status: Vd1AssertionStatus; detail: string };
type Vd1Response = {
  ok: boolean;
  assertions?: Vd1Assertion[];
  teardown?: Teardown;
  error?: string;
  detail?: string;
};

function Vd1Panel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Vd1Response | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number | null>(null);

  const run = async () => {
    setLoading(true); setError(null); setResult(null); setElapsedMs(null);
    const t0 = Date.now();
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke<Vd1Response>(
        'phase4b-vd1-gauntlet', { body: {} },
      );
      if (invokeErr) setError(invokeErr.message || 'Invocation failed');
      else setResult(data ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setElapsedMs(Date.now() - t0);
      setLoading(false);
    }
  };

  const badgeFor = (s: Vd1AssertionStatus) => {
    if (s === 'passed') return <Badge>passed</Badge>;
    if (s === 'failed') return <Badge variant="destructive">failed</Badge>;
    return <Badge variant="secondary">skipped</Badge>;
  };
  const iconFor = (s: Vd1AssertionStatus) => {
    if (s === 'passed') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (s === 'failed') return <XCircle className="h-4 w-4 text-destructive" />;
    return <XCircle className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <section className="space-y-6 pt-4 border-t border-border">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading font-semibold">Phase 4B · V-D1 gauntlet</h2>
          <p className="text-sm text-muted-foreground">
            Runs <code className="text-xs bg-muted px-1 py-0.5 rounded">phase4b-vd1-gauntlet</code>.
            <code className="text-xs bg-muted px-1 py-0.5 rounded">submit_pulse</code> round-trip:
            happy path + write-only-via-DEFINER, value-range CHECK, same-UTC-day UPSERT, cross-org
            block, revoked-membership block. Three skipped contract probes pin the documented
            gaps (no webhook, no parent_pulse backfill, no allowedOrigins pin).
          </p>
        </div>
        <Button onClick={run} disabled={loading} size="lg">
          {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Running…</>) :
            (<><PlayCircle className="h-4 w-4 mr-2" /> Run V-D1</>)}
        </Button>
      </div>

      {elapsedMs !== null && (
        <p className="text-xs text-muted-foreground">Last run: {(elapsedMs / 1000).toFixed(2)}s</p>
      )}

      {error && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base text-destructive">Invocation error</CardTitle>
            <CardDescription className="font-mono text-xs">{error}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {result && (
        <>
          <Card className={result.ok ? 'border-emerald-500/40' : 'border-destructive'}>
            <CardHeader>
              <div className="flex items-center gap-2">
                {result.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> :
                  <XCircle className="h-5 w-5 text-destructive" />}
                <CardTitle className="text-base">
                  {result.ok ? 'All checks passed' : 'Failed'}
                </CardTitle>
              </div>
              {result.error && (
                <CardDescription className="font-mono text-xs">
                  {result.error}{result.detail ? ` — ${result.detail}` : ''}
                </CardDescription>
              )}
            </CardHeader>
          </Card>

          {result.assertions && result.assertions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-heading">Assertions</CardTitle>
                <CardDescription>
                  Persistence contract for <code className="text-xs">submit_pulse</code>.
                  Skipped cases pin documented gaps — they flip to red the moment the gap closes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {result.assertions.map((a) => (
                  <div key={a.name} className="rounded-md border border-border p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {iconFor(a.status)}
                        <span className="font-mono text-sm font-medium">{a.name}</span>
                      </div>
                      {badgeFor(a.status)}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground break-all">{a.detail}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {result.teardown && <TeardownCard td={result.teardown} />}
        </>
      )}
    </section>
  );
}

export default function Gauntlet() {
  return (
    <div className="space-y-10 max-w-4xl">
      <Vc1Panel />
      <Vc2Panel />
      <Vc3Panel />
      <Vc4Panel />
      <Vc4BurstPanel />
      <Vd1Panel />
    </div>
  );
}
